import React from "react";
import { Link } from "react-router-dom";
import { Award, Users, Building, Handshake, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const values = [
  { icon: Handshake, title: "Flexible Terms", body: "Our rates and structures adapt to what landlords and funders actually need." },
  { icon: ShieldCheck, title: "Compliance-First", body: "PPRA registered, B-BBEE Level 100%, and NSFAS-savvy — the boring stuff, done properly." },
  { icon: Users, title: "Student-Centric", body: "We manage properties that students actually want to live in — clean, safe, connected." },
  { icon: Building, title: "National Footprint", body: "Boots on the ground in Johannesburg, Pretoria, Cape Town, Durban, Stellenbosch and more." },
];

export default function AboutPage() {
  return (
    <div>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 md:pt-32">
        <p className="overline text-secondary">About Campusco</p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif leading-tight max-w-4xl">
          A woman-led property firm building South Africa&apos;s most trusted student residences.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-3xl leading-relaxed">
          Campusco (Pty) Ltd. has managed properties since 2017. Our team brings more
          than 25 years of combined experience to residence management, valuations
          and technical services — everything a modern student building needs.
        </p>
      </section>

      {/* Story + image */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-6">
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&auto=format&fit=crop&q=80"
              alt="South African leadership"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="lg:col-span-6">
          <p className="overline text-secondary">Our Story</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl">Built on 25 years of experience — and one very clear conviction.</h2>
          <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Campusco was founded on a simple observation: South Africa&apos;s student housing market
              was long overdue for a manager that treats every building like an institutional asset,
              and every student like a valued resident.
            </p>
            <p>
              With the CEO and CFO both being women, we bring a different lens to how residences are
              designed, run and reported on. Our team draws on more than a quarter-century of combined
              property management, quantity surveying and engineering expertise — the kind of depth
              usually reserved for commercial and industrial real estate.
            </p>
            <p>
              Today, Campusco works with landlords, developers, funders and universities across the
              country. Our rates stay competitive, our reporting stays transparent, and our residences
              stay full.
            </p>
          </div>
        </div>
      </section>

      {/* Values grid */}
      <section className="bg-muted/30 border-y border-border py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="overline text-secondary text-center">What We Stand For</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-center max-w-3xl mx-auto">Values that shape every building we manage.</h2>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="bg-background border border-border p-8">
                  <Icon className="w-8 h-8 text-secondary" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-xl">{v.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <p className="overline text-secondary">Leadership</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl max-w-3xl">Meet the women running the business.</h2>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {[
            { name: "Chief Executive Officer", role: "CEO", img: "https://images.unsplash.com/photo-1758611972971-1c8b9c6d7822?w=800&auto=format&fit=crop&q=80", bio: "Sets Campusco's strategy and drives our national growth in student accommodation." },
            { name: "Chief Financial Officer", role: "CFO", img: "https://images.unsplash.com/photo-1666867540898-aaa1993ffabc?w=800&auto=format&fit=crop&q=80", bio: "Oversees finance, funder relations, valuation quality and financial reporting." },
            { name: "Head of Operations", role: "OPERATIONS", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&auto=format&fit=crop&q=80", bio: "Leads on-the-ground residence teams and building performance across our national portfolio." },
          ].map((p) => (
            <div key={p.name}>
              <div className="aspect-[3/4] overflow-hidden bg-muted">
                <img src={p.img} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <p className="mt-5 overline text-secondary">{p.role}</p>
              <h3 className="mt-2 font-serif text-xl">{p.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.bio}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Accreditations */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-3 gap-10">
          {[
            { title: "B-BBEE Level 100%", body: "Fully compliant contributor recognised by South African transformation standards." },
            { title: "PPRA Registered", body: "Registered with the Property Practitioners Regulatory Authority — with all required Fidelity Fund Certificates." },
            { title: "Woman-Led Enterprise", body: "Both our CEO and CFO are women, and our leadership team reflects the country we serve." },
          ].map((a) => (
            <div key={a.title}>
              <Award className="w-8 h-8 text-secondary" strokeWidth={1.5} />
              <h3 className="mt-6 font-serif text-2xl">{a.title}</h3>
              <p className="mt-3 text-primary-foreground/75 text-sm leading-relaxed">{a.body}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-primary-foreground/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row gap-6 items-center justify-between">
            <p className="text-primary-foreground/80 max-w-2xl">Want to see it all in a single document? Download the Campusco services brochure.</p>
            <div className="flex gap-3">
              <Button asChild className="rounded-sm bg-secondary text-secondary-foreground hover:bg-secondary/90">
                <Link to="/brochure" target="_blank" rel="noopener" data-testid="about-download-brochure">View Brochure</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-sm border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <Link to="/book-appointment" data-testid="about-book-appointment">Book Appointment</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
